import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  AlertCircle,
  Info,
  Play,
  Pause,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  Zap,
  Bot,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { HealthGauge, LiveIndicator, TimeAgo, CorrelationIdBadge } from '../../components/admin/shared';

export default function OperationsCenter() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Queries with auto-refresh
  const { data: liveStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['ops-live-status'],
    queryFn: () => api.getOpsLiveStatus(),
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const { data: runningSeeds, isLoading: seedsLoading } = useQuery({
    queryKey: ['ops-running-seeds'],
    queryFn: () => api.getOpsRunningSeeds(),
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const { data: alerts } = useQuery({
    queryKey: ['ops-alerts'],
    queryFn: () => api.getOpsAlerts(),
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const { data: activityFeed } = useQuery({
    queryKey: ['ops-activity-feed'],
    queryFn: () => api.getOpsActivityFeed(30),
    refetchInterval: autoRefresh ? 10000 : false,
  });

  // Mutations for quick actions
  const watchdogMutation = useMutation({
    mutationFn: () => api.triggerOpsWatchdog(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ops-live-status'] });
      queryClient.invalidateQueries({ queryKey: ['ops-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['ops-running-seeds'] });
    },
  });

  const pauseAllMutation = useMutation({
    mutationFn: () => api.pauseAllSeeds(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ops-live-status'] });
    },
  });

  const resumeAllMutation = useMutation({
    mutationFn: () => api.resumeAllSeeds(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ops-live-status'] });
    },
  });

  const clearStuckMutation = useMutation({
    mutationFn: () => api.clearStuckSeeds(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ops-live-status'] });
      queryClient.invalidateQueries({ queryKey: ['ops-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['ops-running-seeds'] });
    },
  });

  const totalAlerts = (alerts?.alerts?.length || 0);
  const criticalAlerts = alerts?.alerts?.filter(a => a.severity === 'critical') || [];
  const warningAlerts = alerts?.alerts?.filter(a => a.severity === 'warning') || [];

  return (
    <div className="space-y-6">
      {/* Header with auto-refresh toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Operations Center</h2>
          {autoRefresh && <LiveIndicator />}
        </div>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
            transition-colors
            ${autoRefresh
              ? 'bg-green-50 text-green-700 hover:bg-green-100'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
          {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
        </button>
      </div>

      {/* Live Status Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Health Gauges */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">System Health</h3>
          <div className="flex justify-center">
            <HealthGauge
              label="Seed Success"
              value={liveStatus?.seeds.successRate24h || 100}
              size="md"
              subtitle="Last 24 hours"
            />
          </div>
        </div>

        {/* Seed Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Seed Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Running</span>
              <span className="flex items-center gap-2">
                {(liveStatus?.seeds.running || 0) > 0 && (
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                )}
                <span className="text-2xl font-bold text-gray-900">
                  {statusLoading ? '-' : liveStatus?.seeds.running || 0}
                </span>
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Queued</span>
              <span className="text-2xl font-bold text-gray-900">
                {statusLoading ? '-' : liveStatus?.seeds.queued || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Errors (1h)</span>
              <span className={`text-2xl font-bold ${(liveStatus?.seeds.errorsLast1h || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {statusLoading ? '-' : liveStatus?.seeds.errorsLast1h || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Data Quality */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Data Quality</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Updated (24h)</span>
              <span className="text-lg font-semibold text-gray-900">
                {statusLoading ? '-' : (liveStatus?.data.productsUpdated24h || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Stale</span>
              <span className={`text-lg font-semibold ${(liveStatus?.data.staleProducts || 0) > 100 ? 'text-amber-600' : 'text-gray-900'}`}>
                {statusLoading ? '-' : (liveStatus?.data.staleProducts || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Coverage</span>
              <span className="text-lg font-semibold text-gray-900">
                {statusLoading ? '-' : `${liveStatus?.data.storesCovered || 0}/${liveStatus?.data.totalStores || 0}`}
              </span>
            </div>
          </div>
        </div>

        {/* Alerts Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Active Alerts</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                Critical
              </span>
              <span className={`text-2xl font-bold ${criticalAlerts.length > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {criticalAlerts.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                Warning
              </span>
              <span className={`text-2xl font-bold ${warningAlerts.length > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                {warningAlerts.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-blue-600">
                <Info className="w-4 h-4" />
                Info
              </span>
              <span className="text-2xl font-bold text-gray-900">
                {liveStatus?.alerts.info || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => watchdogMutation.mutate()}
            disabled={watchdogMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            <Zap className="w-4 h-4" />
            {watchdogMutation.isPending ? 'Running...' : 'Run Watchdog'}
          </button>
          <button
            onClick={() => pauseAllMutation.mutate()}
            disabled={pauseAllMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            <Pause className="w-4 h-4" />
            {pauseAllMutation.isPending ? 'Pausing...' : 'Pause All'}
          </button>
          <button
            onClick={() => resumeAllMutation.mutate()}
            disabled={resumeAllMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            {resumeAllMutation.isPending ? 'Resuming...' : 'Resume All'}
          </button>
          <button
            onClick={() => clearStuckMutation.mutate()}
            disabled={clearStuckMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {clearStuckMutation.isPending ? 'Clearing...' : 'Clear Stuck'}
          </button>
        </div>
        {/* Mutation results */}
        {watchdogMutation.data && (
          <p className="mt-2 text-sm text-green-600">
            Watchdog: recovered {watchdogMutation.data.recovered}, paused {watchdogMutation.data.paused}, cleaned {watchdogMutation.data.expiredLocksCleaned} locks
          </p>
        )}
        {pauseAllMutation.data && (
          <p className="mt-2 text-sm text-amber-600">
            Paused {pauseAllMutation.data.pausedCount} schedules
          </p>
        )}
        {resumeAllMutation.data && (
          <p className="mt-2 text-sm text-green-600">
            Resumed {resumeAllMutation.data.resumedCount} schedules
          </p>
        )}
        {clearStuckMutation.data && (
          <p className="mt-2 text-sm text-red-600">
            Cleared {clearStuckMutation.data.clearedCount} stuck schedules
          </p>
        )}
      </div>

      {/* Two column layout for running seeds and alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Running Seeds */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Running Seeds</h3>
            <Link
              to="/admin/seeds"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {seedsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (runningSeeds?.seeds?.length || 0) === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bot className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No seeds currently running</p>
            </div>
          ) : (
            <div className="space-y-4">
              {runningSeeds?.seeds.map((seed) => (
                <div
                  key={seed.scheduleId}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4 text-primary-600" />
                      <span className="font-medium text-gray-900">{seed.seedName}</span>
                      <span className="text-xs text-gray-500">({seed.runType})</span>
                    </div>
                    <span className="text-sm text-gray-500">{seed.durationMinutes}m</span>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{seed.storesCompleted}/{seed.totalStores} stores</span>
                      <span>{seed.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${seed.progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      {seed.currentPhase || 'Starting...'}
                    </span>
                    {seed.correlationId && (
                      <CorrelationIdBadge correlationId={seed.correlationId} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Active Alerts</h3>
            <span className="text-sm text-gray-400">{totalAlerts} total</span>
          </div>

          {totalAlerts === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
              <p>All systems operational</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {alerts?.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`
                    rounded-lg p-3 border
                    ${alert.severity === 'critical'
                      ? 'bg-red-50 border-red-200'
                      : alert.severity === 'warning'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-blue-50 border-blue-200'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {alert.severity === 'critical' ? (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      ) : alert.severity === 'warning' ? (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      ) : (
                        <Info className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${
                          alert.severity === 'critical'
                            ? 'text-red-900'
                            : alert.severity === 'warning'
                            ? 'text-amber-900'
                            : 'text-blue-900'
                        }`}>
                          {alert.title}
                        </span>
                        <TimeAgo
                          timestamp={alert.timestamp}
                          className="text-xs"
                        />
                      </div>
                      <p className={`text-sm mt-0.5 ${
                        alert.severity === 'critical'
                          ? 'text-red-700'
                          : alert.severity === 'warning'
                          ? 'text-amber-700'
                          : 'text-blue-700'
                      }`}>
                        {alert.message}
                      </p>
                      {alert.actionUrl && (
                        <Link
                          to={alert.actionUrl}
                          className="text-xs text-primary-600 hover:underline mt-1 inline-block"
                        >
                          View details
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 mb-4">Recent Activity</h3>

        {(activityFeed?.activities?.length || 0) === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {activityFeed?.activities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {activity.type === 'seed_complete' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : activity.type === 'seed_error' ? (
                    <XCircle className="w-4 h-4 text-red-500" />
                  ) : activity.type === 'seed_start' ? (
                    <Play className="w-4 h-4 text-blue-500" />
                  ) : (
                    <Activity className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {activity.title}
                    </span>
                    <TimeAgo
                      timestamp={activity.timestamp}
                      className="text-xs"
                    />
                  </div>
                  <p className="text-sm text-gray-500 truncate">
                    {activity.details}
                  </p>
                  {activity.correlationId && (
                    <div className="mt-1">
                      <CorrelationIdBadge correlationId={activity.correlationId} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
